import React from 'react'
import ColorInput from '@app/components/molecules/TeeColorPicker/CustomColorInput'
import * as S from './TeeRow.styles'

export { TeeGrid } from './TeeRow.styles'

// The tee as it is being filled in: the two ratings are held as typed so a
// field can be emptied and retyped without turning into a zero in between.
export type TeeRowValue = {
  title: string
  color: string
  courseRating: string
  slopeRating: string
}

const isFilled = (value: string) => value.trim() !== '' && Number(value) > 0

export const isTeeRowComplete = (row: TeeRowValue) =>
  row.title.trim() !== '' &&
  isFilled(row.courseRating) &&
  isFilled(row.slopeRating)

// Slope rating runs from 55 to 155 under the WHS, so a number outside that is
// put back on the scale rather than kept as it stands. An empty field is left
// empty, so it can still be cleared and retyped and still counts as missing.
export const MIN_SLOPE_RATING = 55
export const MAX_SLOPE_RATING = 155

const clampSlopeRating = (value: string) => {
  const slope = Number(value)
  if (value.trim() === '' || Number.isNaN(slope)) return value
  return String(Math.min(Math.max(slope, MIN_SLOPE_RATING), MAX_SLOPE_RATING))
}

// The single tee and the set of tees are both saved from here, so the slope
// rating is put on the scale on the way out too: a number typed and saved
// without the field ever being left is still saved in range.
export const teeFromRow = (row: TeeRowValue) => ({
  title: row.title.trim(),
  courseRating: Number(row.courseRating),
  slopeRating: Number(clampSlopeRating(row.slopeRating)),
  color: row.color,
})

export const TeeRowHeader = () => (
  <>
    <S.ColumnLabel>Color</S.ColumnLabel>
    <S.ColumnLabel>Tee name</S.ColumnLabel>
    <S.ColumnLabel>Course rating</S.ColumnLabel>
    <S.ColumnLabel>
      Slope rating
      <S.ColumnHint>
        {MIN_SLOPE_RATING}-{MAX_SLOPE_RATING}
      </S.ColumnHint>
    </S.ColumnLabel>
  </>
)

type Props = {
  // What the fields of this row are called to a screen reader, such as
  // "Tee 2" in a set or just "Tee" on its own.
  label: string
  value: TeeRowValue
  onChange: (update: Partial<TeeRowValue>) => void
  // Set once adding has been tried, so a row does not open marked red.
  showErrors?: boolean
}

export const TeeRow: React.FC<Props> = ({
  label,
  value,
  onChange,
  showErrors,
}) => (
  <>
    <S.ColorCell>
      <ColorInput
        value={value.color}
        label={`Color for ${label.toLowerCase()}`}
        onChange={(color) => onChange({ color })}
      />
    </S.ColorCell>
    <S.NameInput
      value={value.title}
      aria-label={`${label} name`}
      hasError={showErrors && value.title.trim() === ''}
      onChange={({ target }) => onChange({ title: target.value })}
    />
    <S.NumberInput
      type="number"
      step="0.1"
      value={value.courseRating}
      aria-label={`${label} course rating`}
      hasError={showErrors && !isFilled(value.courseRating)}
      onChange={({ target }) => onChange({ courseRating: target.value })}
    />
    <S.NumberInput
      type="number"
      min={MIN_SLOPE_RATING}
      max={MAX_SLOPE_RATING}
      value={value.slopeRating}
      aria-label={`${label} slope rating`}
      hasError={showErrors && !isFilled(value.slopeRating)}
      onChange={({ target }) => onChange({ slopeRating: target.value })}
      // Put right once the field is left, so a 120 typed digit by digit is not
      // pulled up to 55 after the first one.
      onBlur={({ target }) =>
        onChange({ slopeRating: clampSlopeRating(target.value) })
      }
    />
  </>
)

export default TeeRow
